// Settings Page - Configuration Wizard
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useUserConfig from '../hooks/useUserConfig';
import './Settings.css';

// Provider Logos (SVG inline for better control)
const GitHubLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
  </svg>
);

const SupabaseLogo = ({ className }) => (
  <svg viewBox="0 0 109 113" fill="currentColor" className={className}>
    <path d="M63.7076 110.284C60.8481 113.885 55.0502 111.912 54.9813 107.314L53.9738 40.0627L99.1935 40.0627C107.384 40.0627 111.952 49.5228 106.859 55.9374L63.7076 110.284Z"/>
    <path d="M63.7076 110.284C60.8481 113.885 55.0502 111.912 54.9813 107.314L53.9738 40.0627L99.1935 40.0627C107.384 40.0627 111.952 49.5228 106.859 55.9374L63.7076 110.284Z" fillOpacity="0.2"/>
    <path d="M45.317 2.07103C48.1765 -1.53037 53.9745 0.442937 54.0434 5.041L54.4849 72.2922H9.83113C1.64038 72.2922 -2.92775 62.8321 2.1655 56.4175L45.317 2.07103Z"/>
  </svg>
);

const Settings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    config,
    loading,
    isCreator,
    connectSupabase,
    disconnectSupabase,
    setGitHubRepo,
    setBackupPrefix,
    updateConfig,
    completeSetup,
    setSetupStep,
    getBackupBranchName,
    isFullSetupDone
  } = useUserConfig(user);

  const [step, setStep] = useState(config.setupStep || 1);
  const [supabaseUrl, setSupabaseUrl] = useState(config.supabaseUrl || '');
  const [supabaseKey, setSupabaseKey] = useState(config.supabaseKey || '');
  const [selectedRepo, setSelectedRepo] = useState(config.githubRepo || '');
  const [branchPrefix, setBranchPrefix] = useState(config.backupBranchPrefix || 'backup');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [repos, setRepos] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);

  const totalSteps = 3;

  // Load user's GitHub repos
  useEffect(() => {
    if (user && step === 2) {
      loadGitHubRepos();
    }
  }, [user, step]);

  const loadGitHubRepos = async () => {
    setLoadingRepos(true);
    try {
      // Get repos from GitHub API using user's access token
      // For now, we'll use a placeholder - in production, use the OAuth token
      const mockRepos = [
        { name: 'my-project', full_name: `${user?.user_metadata?.user_name || 'user'}/my-project` },
        { name: 'another-repo', full_name: `${user?.user_metadata?.user_name || 'user'}/another-repo` },
      ];
      setRepos(mockRepos);
    } catch (err) {
      console.error('Error loading repos:', err);
    } finally {
      setLoadingRepos(false);
    }
  };

  const handleConnectSupabase = async () => {
    setConnecting(true);
    setError('');
    setSuccess('');

    const result = await connectSupabase(supabaseUrl, supabaseKey);

    if (result.success) {
      setSuccess('¡Conectado exitosamente! Creando tablas...');
      // Auto advance to next step after short delay
      setTimeout(() => {
        setStep(2);
        setSetupStep(2);
        setSuccess('');
      }, 1500);
    } else {
      setError(result.error);
    }

    setConnecting(false);
  };

  const handleSelectRepo = (repoFullName) => {
    const [owner, repo] = repoFullName.split('/');
    setSelectedRepo(repoFullName);
    setGitHubRepo(owner, repo);
  };

  const handleNextFromGitHub = () => {
    if (selectedRepo) {
      setStep(3);
      setSetupStep(3);
    }
  };

  const handleFinish = () => {
    setBackupPrefix(branchPrefix);
    completeSetup();
    navigate('/dashboard');
  };

  const handleSkipGitHub = () => {
    setStep(3);
    setSetupStep(3);
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="settings-loading">
          <div className="loading-spinner"></div>
          <p>Cargando configuración...</p>
        </div>
      </div>
    );
  }

  // If creator account and fully configured, show success state
  if (isCreator && config.setupCompleted) {
    return (
      <div className="settings-container">
        <div className="settings-bg">
          <div className="settings-grid-overlay"></div>
        </div>

        <nav className="settings-nav">
          <Link to="/dashboard" className="settings-back">
            ← Volver al Dashboard
          </Link>
          <div className="settings-user">
            <span className="creator-badge">👑 Creador</span>
            <img 
              src={user?.user_metadata?.avatar_url || 'https://github.com/ghost.png'} 
              alt="Avatar" 
              className="settings-avatar"
            />
            <span>@{user?.user_metadata?.user_name || 'usuario'}</span>
          </div>
        </nav>

        <main className="settings-main">
          <div className="settings-header">
            <h1>⚙️ Configuración</h1>
            <p>Tu espacio de trabajo está completamente configurado</p>
          </div>

          <div className="settings-step creator-configured">
            <div className="creator-success-icon">✅</div>
            <h2>¡Todo configurado!</h2>
            <p>Como creador de la plataforma, tu cuenta tiene acceso completo pre-configurado.</p>

            <div className="creator-config-summary">
              <div className="config-item">
                <span className="config-icon">🗄️</span>
                <div>
                  <strong>Supabase</strong>
                  <p>{config.supabaseUrl}</p>
                </div>
                <span className="config-status connected">✅ Conectado</span>
              </div>

              <div className="config-item">
                <span className="config-icon">🐙</span>
                <div>
                  <strong>GitHub</strong>
                  <p>{config.githubOwner}/{config.githubRepo}</p>
                </div>
                <span className="config-status connected">✅ Conectado</span>
              </div>

              <div className="config-item">
                <span className="config-icon">💾</span>
                <div>
                  <strong>Prefijo de Backup</strong>
                  <p>{config.backupBranchPrefix}_DDMMYY_HHMM</p>
                </div>
                <span className="config-status connected">✅ Configurado</span>
              </div>

              <div className="config-item">
                <span className="config-icon">🌐</span>
                <div>
                  <strong>Proyecto</strong>
                  <p>{config.projectName} - {config.projectWebUrl}</p>
                </div>
                <span className="config-status connected">✅ Activo</span>
              </div>
            </div>

            <div className="step-actions">
              <Link to="/dashboard" className="btn-finish">
                ← Volver al Dashboard
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-bg">
        <div className="settings-grid-overlay"></div>
      </div>

      <nav className="settings-nav">
        <Link to="/dashboard" className="settings-back">
          ← Volver al Dashboard
        </Link>
        <div className="settings-user">
          <img 
            src={user?.user_metadata?.avatar_url || 'https://github.com/ghost.png'} 
            alt="Avatar" 
            className="settings-avatar"
          />
          <span>@{user?.user_metadata?.user_name || 'usuario'}</span>
        </div>
      </nav>

      <main className="settings-main">
        <div className="settings-header">
          <h1>⚙️ Configuración</h1>
          <p>Conecta tus servicios para empezar a crear proyectos</p>
        </div>

        {/* Progress Steps */}
        <div className="settings-progress">
          {[1, 2, 3].map(s => (
            <div 
              key={s} 
              className={`progress-step ${s === step ? 'active' : ''} ${s < step ? 'completed' : ''}`}
              onClick={() => s < step && setStep(s)}
            >
              <div className="step-number">{s < step ? '✓' : s}</div>
              <div className="step-label">
                {s === 1 && 'Supabase'}
                {s === 2 && 'GitHub'}
                {s === 3 && 'Backups'}
              </div>
            </div>
          ))}
          <div className="progress-line">
            <div 
              className="progress-line-fill" 
              style={{ width: `${((step - 1) / (totalSteps - 1)) * 100}%` }}
            />
          </div>
        </div>

        {/* Step 1: Supabase */}
        {step === 1 && (
          <div className="settings-step">
            <div className="step-header">
              <SupabaseLogo className="provider-logo supabase" />
              <div>
                <h2>Conecta tu Supabase</h2>
                <p>Base de datos para guardar tus proyectos</p>
              </div>
            </div>

            {config.supabaseConnected ? (
              <div className="connected-box">
                <div className="connected-status">
                  <span className="status-icon">✅</span>
                  <div>
                    <strong>Conectado</strong>
                    <p>{config.supabaseUrl}</p>
                  </div>
                </div>
                <button className="btn-disconnect" onClick={disconnectSupabase}>
                  Desconectar
                </button>
              </div>
            ) : (
              <>
                <div className="settings-instructions">
                  <h4>📋 Pasos para obtener tus credenciales:</h4>
                  <ol>
                    <li>
                      Ve a <a href="https://supabase.com/dashboard" target="_blank" rel="noopener noreferrer">supabase.com/dashboard</a>
                    </li>
                    <li>Crea un proyecto nuevo o selecciona uno existente</li>
                    <li>Ve a <strong>Settings → API</strong></li>
                    <li>Copia los valores de abajo</li>
                  </ol>
                </div>

                <div className="settings-form">
                  <div className="form-group">
                    <label>
                      <span className="label-icon">🔗</span>
                      Project URL
                    </label>
                    <input
                      type="url"
                      value={supabaseUrl}
                      onChange={(e) => setSupabaseUrl(e.target.value)}
                      placeholder="https://xxxxx.supabase.co"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <span className="label-icon">🔑</span>
                      Anon Key (public)
                    </label>
                    <input
                      type="password"
                      value={supabaseKey}
                      onChange={(e) => setSupabaseKey(e.target.value)}
                      placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                    />
                    <span className="input-hint">Usa la "anon" key, no la "service_role"</span>
                  </div>

                  {error && <div className="settings-error">⚠️ {error}</div>}
                  {success && <div className="settings-success">✅ {success}</div>}

                  <button 
                    className="btn-connect"
                    onClick={handleConnectSupabase}
                    disabled={connecting || !supabaseUrl || !supabaseKey}
                  >
                    {connecting ? (
                      <>
                        <span className="btn-spinner"></span>
                        Conectando...
                      </>
                    ) : (
                      <>🔌 Conectar y Crear Tablas</>
                    )}
                  </button>
                </div>

                <div className="no-account-box">
                  <p>¿No tienes cuenta de Supabase?</p>
                  <a href="https://supabase.com" target="_blank" rel="noopener noreferrer">
                    Crear cuenta gratis →
                  </a>
                </div>
              </>
            )}

            {config.supabaseConnected && (
              <div className="step-actions">
                <button className="btn-next" onClick={() => { setStep(2); setSetupStep(2); }}>
                  Siguiente: GitHub →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: GitHub */}
        {step === 2 && (
          <div className="settings-step">
            <div className="step-header">
              <GitHubLogo className="provider-logo github" />
              <div>
                <h2>Selecciona tu Repositorio</h2>
                <p>Para backups automáticos de tu código</p>
              </div>
            </div>

            <div className="github-connected">
              <img 
                src={user?.user_metadata?.avatar_url || 'https://github.com/ghost.png'} 
                alt="Avatar"
              />
              <div>
                <strong>Conectado como @{user?.user_metadata?.user_name}</strong>
                <p>via GitHub OAuth</p>
              </div>
            </div>

            <div className="repo-selector">
              <label>Selecciona el repositorio para backups:</label>
              
              {loadingRepos ? (
                <div className="loading-repos">
                  <span className="btn-spinner"></span>
                  Cargando repositorios...
                </div>
              ) : (
                <div className="repo-list">
                  <div className="repo-input-wrapper">
                    <input
                      type="text"
                      value={selectedRepo}
                      onChange={(e) => setSelectedRepo(e.target.value)}
                      placeholder="usuario/nombre-repo"
                      className="repo-input"
                    />
                    <span className="repo-hint">Escribe el nombre completo: usuario/repositorio</span>
                  </div>
                </div>
              )}
            </div>

            <div className="step-actions">
              <button className="btn-back" onClick={() => { setStep(1); setSetupStep(1); }}>
                ← Atrás
              </button>
              <button className="btn-skip" onClick={handleSkipGitHub}>
                Saltar por ahora
              </button>
              <button 
                className="btn-next" 
                onClick={handleNextFromGitHub}
                disabled={!selectedRepo}
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Backup Config */}
        {step === 3 && (
          <div className="settings-step">
            <div className="step-header">
              <div className="provider-logo backup">💾</div>
              <div>
                <h2>Configuración de Backups</h2>
                <p>Personaliza cómo se crean tus backups</p>
              </div>
            </div>

            <div className="settings-form">
              <div className="form-group">
                <label>
                  <span className="label-icon">🏷️</span>
                  Prefijo para ramas de backup
                </label>
                <input
                  type="text"
                  value={branchPrefix}
                  onChange={(e) => setBranchPrefix(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                  placeholder="mi_proyecto_backup"
                />
                <span className="input-hint">Solo letras, números, guiones y guiones bajos</span>
              </div>

              <div className="branch-preview">
                <label>📌 Vista previa de nombre de rama:</label>
                <code>{branchPrefix}_020225_1645</code>
              </div>

              <div className="backup-options">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={config.includeDatabase}
                    onChange={(e) => updateConfig('includeDatabase', e.target.checked)}
                  />
                  <span className="checkbox-custom"></span>
                  Incluir base de datos en backup
                </label>
                
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={config.createBranch}
                    onChange={(e) => updateConfig('createBranch', e.target.checked)}
                  />
                  <span className="checkbox-custom"></span>
                  Crear rama en GitHub
                </label>
                
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={config.downloadZip}
                    onChange={(e) => updateConfig('downloadZip', e.target.checked)}
                  />
                  <span className="checkbox-custom"></span>
                  Descargar ZIP del código
                </label>
              </div>
            </div>

            <div className="setup-summary">
              <h4>📋 Resumen de configuración:</h4>
              <div className="summary-items">
                <div className="summary-item">
                  <SupabaseLogo className="summary-icon" />
                  <span>{config.supabaseConnected ? '✅ Conectado' : '⚪ No configurado'}</span>
                </div>
                <div className="summary-item">
                  <GitHubLogo className="summary-icon" />
                  <span>{config.githubRepo ? `✅ ${config.githubRepo}` : '⚪ No configurado'}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-emoji">💾</span>
                  <span>Prefijo: {branchPrefix}</span>
                </div>
              </div>
            </div>

            <div className="step-actions">
              <button className="btn-back" onClick={() => { setStep(2); setSetupStep(2); }}>
                ← Atrás
              </button>
              <button className="btn-finish" onClick={handleFinish}>
                ✅ Finalizar Configuración
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Settings;
